import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { generateFilterPayloadForReviewPendingOrders } from "../utils/generateFilterPayloadForReviewPendingOrders";
import { useFilterPayload } from "./useFilterPayload";
import { ReviewPendingOrdersState } from "./useReviewPendingOrdersTableData";

/**
 * Handles infinite-scroll pagination for Review Pending Orders. When the table's visible
 * end index reaches the last loaded hit and more pages are available, automatically
 * fetches the next page and appends the results.
 *
 * @param validFilters - Currently applied filter values
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param searchTerm - Free-text search string
 * @param reviewPendingOrdersState - Current orders state (used to determine page boundaries)
 * @param setReviewPendingOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @param tableName - Table context key for tracking the last batch fetched
 * @param authStatus - Optional auth status segment to scope the payload
 * @returns endIndex and setEndIndex to track the table's visible boundary
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  reviewPendingOrdersState: ReviewPendingOrdersState,
  setReviewPendingOrdersState: React.Dispatch<
    React.SetStateAction<ReviewPendingOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  tableName?: string,
  authStatus?: string,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(
    tableName ?? TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    authStatus,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setReviewPendingOrdersStateRef = useRef(setReviewPendingOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setReviewPendingOrdersStateRef.current = setReviewPendingOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setReviewPendingOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (reviewPendingOrdersState.data?.hits?.length ?? 0) - 1 &&
      (reviewPendingOrdersState.data?.page ?? 0) <
        (reviewPendingOrdersState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    reviewPendingOrdersState.data?.hits?.length,
    reviewPendingOrdersState.data?.page,
    reviewPendingOrdersState.data?.total_pages,
  ]);

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return generateFilterPayloadForReviewPendingOrders(
        validFilters,
        searchTerm,
        getOrgIdForFetchExternalWorklist(),
        user?.id ?? "",
        authStatus,
        sortByField,
        sortAscending,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    searchTerm,
    user?.id,
    authStatus,
    sortByField,
    sortAscending,
  ]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (reviewPendingOrdersState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, reviewPendingOrdersState.data?.page],
    );

  const currentPage = reviewPendingOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setReviewPendingOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(
      tableName ?? TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA,
      {
        lastBatchFetched: currentPage + 1,
      },
    );
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
